export const applicationPages = {
  "home-garden": {
    slug: "home-garden",
    title: "Home & Garden",
    eyebrow: "Applications",
    heroTitle: "Healthy Plants Start With Better Soil",
    heroAccent: "At Home.",
    heroText: "Organic nutrition for balcony planters, kitchen gardens, flowering pots, lawns, and backyard beds. Built for growers who want cleaner inputs and visible results.",
    heroBackground: "https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?auto=format&fit=crop&w=1800&q=80",
    heroCtas: [
      { href: "/vermicompost", label: "Shop Vermicompost", variant: "gold" },
      { href: "/vermiwash", label: "Shop Vermiwash", variant: "outline" }
    ],
    sections: [
      {
        imageSide: "left",
        image: "https://images.unsplash.com/photo-1471193945509-9ad0617afabf?auto=format&fit=crop&w=1200&q=80",
        imageAlt: "Balcony and terrace garden plants",
        eyebrow: "Everyday Gardening",
        title: "Made for daily plant care and easy home use",
        paragraphs: [
          "For home growers, the big win is consistency. Vermicompost builds richer potting media while Vermiwash gives a simple liquid routine for leaves and active growth.",
          "That means healthier foliage, improved flowering, and fewer swings caused by weak soil or irregular feeding."
        ],
        bullets: [
          "Works for pots, raised beds, herbs, foliage, and ornamentals",
          "Easy to apply without harsh chemical smell",
          "Suitable for repeat use through the growing season"
        ]
      },
      {
        imageSide: "right",
        image: "https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=1200&q=80",
        imageAlt: "Gardener caring for home plants",
        eyebrow: "Best Use Cases",
        title: "Strong support for flowering, fruiting, and foliage plants",
        paragraphs: [
          "Home and kitchen gardens respond especially well when solid and liquid organic inputs are used together. One improves soil structure while the other supports quick, gentle feeding.",
          "It is a practical system for vegetable planters, flowering pots, small lawns, and seasonal decorative beds."
        ],
        bullets: [
          "Useful before re-potting or seasonal refresh",
          "Helps revive tired balcony containers",
          "Supports greener leaves and improved root activity"
        ]
      }
    ],
    banner: {
      eyebrow: "Grow Naturally",
      title: "A cleaner routine for vegetables, flowers, herbs, and ornamental greens",
      text: "Use Paras Organics to simplify feeding, improve soil health, and keep home spaces productive without leaning on aggressive chemical fertilizers.",
      backgroundImage: "https://images.unsplash.com/photo-1501004318641-b39e6451bec6?auto=format&fit=crop&w=1600&q=80"
    },
    videos: [
      { id: "sjhBWhUl8Io", title: "Organic care basics for home gardeners" },
      { id: "vFlxV2DXEyM", title: "Simple plant nutrition support for healthy growth" }
    ]
  },
  agriculture: {
    slug: "agriculture",
    title: "Agriculture",
    eyebrow: "Applications",
    heroTitle: "Organic Support For",
    heroAccent: "Productive Fields.",
    heroText: "A practical organic approach for field crops, orchard basins, vegetable rows, and farm systems that need stronger soil biology and better moisture balance.",
    heroBackground: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1800&q=80",
    heroCtas: [
      { href: "/vermicompost", label: "View Vermicompost", variant: "gold" },
      { href: "/vermiwash", label: "View Vermiwash", variant: "outline" }
    ],
    sections: [
      {
        imageSide: "left",
        image: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1200&q=80",
        imageAlt: "Healthy agricultural crop rows",
        eyebrow: "Field Performance",
        title: "Built for stronger root zones and steadier crop response",
        paragraphs: [
          "In agriculture, improved soil condition matters just as much as nutrient supply. Vermicompost helps build structure, aeration, and microbial activity around active roots.",
          "Vermiwash complements that with flexible liquid application during vegetative growth, flowering, and crop recovery phases."
        ],
        bullets: [
          "Useful across orchards, vegetables, and row crops",
          "Supports better water-holding capacity in active soils",
          "Fits growers moving toward lower-residue input systems"
        ]
      },
      {
        imageSide: "right",
        image: "https://images.unsplash.com/photo-1464226184884-fa280b87c399?auto=format&fit=crop&w=1200&q=80",
        imageAlt: "Farm field with structured crop rows",
        eyebrow: "Operational Fit",
        title: "Easy to work into regular farm routines",
        paragraphs: [
          "Apply vermicompost during land preparation, transplanting, or root-zone strengthening stages. Use Vermiwash for low-dose foliar or drench support as crop needs evolve.",
          "This makes the system practical for repeated use without redesigning the entire farm workflow."
        ],
        bullets: [
          "Useful before irrigation in crop basins and rows",
          "Suitable for repeat foliar schedules in cooler hours",
          "Supports organic and integrated farm management programs"
        ]
      }
    ],
    banner: {
      eyebrow: "Farm Scale Benefits",
      title: "Stronger soil, more resilient crops, and a cleaner input strategy",
      text: "Paras Organics products are designed to support root activity, microbial life, and steady crop development across diverse agricultural conditions.",
      backgroundImage: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1600&q=80"
    },
    videos: [
      { id: "vFlxV2DXEyM", title: "Liquid organic support for crop growth stages" },
      { id: "sjhBWhUl8Io", title: "Soil-first organic fertility fundamentals" }
    ]
  },
  "nursery-landscaping": {
    slug: "nursery-landscaping",
    title: "Nursery & Landscaping",
    eyebrow: "Applications",
    heroTitle: "Reliable Organic Care For",
    heroAccent: "Nursery & Landscaping.",
    heroText: "A balanced organic program for nursery stock, transplanting media, ornamental beds, lawns, and designed landscape spaces that need uniform quality.",
    heroBackground: "https://images.unsplash.com/photo-1512428813834-c702c7702b78?auto=format&fit=crop&w=1800&q=80",
    heroCtas: [
      { href: "/vermicompost", label: "Use Vermicompost", variant: "gold" },
      { href: "/vermiwash", label: "Use Vermiwash", variant: "outline" }
    ],
    sections: [
      {
        imageSide: "left",
        image: "https://images.unsplash.com/photo-1512428813834-c702c7702b78?auto=format&fit=crop&w=1200&q=80",
        imageAlt: "Ornamental nursery and landscaped greens",
        eyebrow: "Nursery Use",
        title: "Helps nursery plants establish with healthier media",
        paragraphs: [
          "Nursery plants need predictable root development, moisture balance, and gentle nutrition. Vermicompost improves media body and microbial health while supporting repeat potting operations.",
          "That helps produce sturdier transplants and more stable growth before dispatch or field planting."
        ],
        bullets: [
          "Useful for potting mixes and transplant stages",
          "Supports root activity in high-turnover nursery systems",
          "Suitable for ornamental and productive plant stock"
        ]
      },
      {
        imageSide: "right",
        image: "https://images.unsplash.com/photo-1501004318641-b39e6451bec6?auto=format&fit=crop&w=1200&q=80",
        imageAlt: "Landscaped public green space and beds",
        eyebrow: "Landscape Maintenance",
        title: "Supports visual quality across lawns, borders, and planted zones",
        paragraphs: [
          "Landscaping teams need inputs that are easy to handle and repeat across multiple spaces. Vermicompost works well during bed preparation and seasonal replenishment.",
          "Vermiwash can support maintenance schedules where foliage quality, recovery, and uniform vigor matter."
        ],
        bullets: [
          "Useful for shrub beds, avenue plantations, and lawn edges",
          "Helps maintain ornamental color and density",
          "Fits routine care for residential and institutional greens"
        ]
      }
    ],
    banner: {
      eyebrow: "Landscape Quality",
      title: "Clean nutrition support for nurseries, lawns, borders, and decorative greens",
      text: "Use Paras Organics to build healthier growing media and maintain greener, better-looking spaces with an organic-first care routine.",
      backgroundImage: "https://images.unsplash.com/photo-1501004318641-b39e6451bec6?auto=format&fit=crop&w=1600&q=80"
    },
    videos: [
      { id: "sjhBWhUl8Io", title: "Organic growing support for nursery and landscape teams" },
      { id: "vFlxV2DXEyM", title: "Flexible foliar and soil applications for green spaces" }
    ]
  }
};
